import { useEffect, useMemo, useState } from 'react'

import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Search, type SearchOption } from '@/components/ui/Search'
import { FormCapsLabel } from '@/components/ui/form-caps-label'
import { formatAssignedUser } from '@/lib/jobAssignments'
import type { User } from '@/db/domain/models'

export function JobAssignmentDialog(props: {
  open: boolean
  onOpenChange: (open: boolean) => void
  jobLabel: string
  users: User[]
  currentUserId?: string
  assignedUserId?: string
  onAssign: (userId: string) => void | Promise<void>
}) {
  const { open, onOpenChange, jobLabel, users, currentUserId, assignedUserId, onAssign } = props
  const [selectedUser, setSelectedUser] = useState<SearchOption | null>(null)
  const [isSaving, setIsSaving] = useState(false)

  const options = useMemo(
    () => users.map((user) => ({ id: user.id, name: user.name } satisfies SearchOption)),
    [users],
  )

  useEffect(() => {
    const current = options.find((option) => option.id === assignedUserId) ?? null
    setSelectedUser(current)
  }, [assignedUserId, options, open])

  const selectedUserId = typeof selectedUser?.id === 'string' ? selectedUser.id : undefined
  const canAssign = Boolean(selectedUserId) && !isSaving

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Assign job</DialogTitle>
          <DialogDescription>
            Pick who should own {jobLabel}.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700">
            <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Current assignment</div>
            <div className="mt-1 font-medium">{formatAssignedUser(users.find((user) => user.id === assignedUserId))}</div>
          </div>

          {currentUserId ? (
            <button
              type="button"
              className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-left text-sm font-medium text-slate-700 hover:bg-slate-50"
              onClick={() => setSelectedUser(options.find((option) => option.id === currentUserId) ?? null)}
            >
              Assign to me
            </button>
          ) : null}

          <div className="space-y-2">
            <FormCapsLabel>Assign to</FormCapsLabel>
            <Search
              options={options}
              value={selectedUser}
              onChange={setSelectedUser}
              ariaLabel="Assign job to user"
              allowCreateOption={false}
            />
          </div>
        </div>

        <DialogFooter>
          <button
            type="button"
            className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
            disabled={isSaving}
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </button>
          <button
            type="button"
            className="inline-flex items-center gap-2 rounded-md bg-slate-900 px-3 py-1.5 text-xs font-medium text-white hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-50"
            disabled={!canAssign}
            onClick={async () => {
              if (!selectedUserId) return
              setIsSaving(true)
              try {
                await Promise.resolve(onAssign(selectedUserId))
                onOpenChange(false)
              } finally {
                setIsSaving(false)
              }
            }}
          >
            {isSaving ? 'Saving…' : 'Save assignment'}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
